import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import RefreshIcon from '@mui/icons-material/Refresh'
import BarChartIcon from '@mui/icons-material/BarChart'
import PieChartIcon from '@mui/icons-material/PieChart'
import TableChartIcon from '@mui/icons-material/TableChart'
import LanguageIcon from '@mui/icons-material/Language'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  LinearProgress,
  Chip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import NoDataImg from '../../../assets/img/no-data.gif'
import NavBar from '../../structure/NavBar'
import { NavBarTop } from '../../structure/NavBarTop'
import urls from '../../constants/apiurl'
import ApiCaller from '../../constants/ApiCaller'
import { showDangerToast } from '../../constants/toaster'
import { formatIndianNumber } from '../../lib/formatIndianNumber'
import { buildTabCacheKey, getTabCache, setTabCache } from '../../lib/tabFetchCache'
import {
  updateTopHeader,
  uplodateLoading
} from '../../../redux/features/common/commonSlice'

const CHART_COLORS = [
  '#5e72e4',
  '#11cdef',
  '#2dce89',
  '#fb6340',
  '#f5365c',
  '#8965e0',
  '#ffd600',
  '#172b4d',
  '#adb5bd',
  '#009688'
]

const PIE_TOP_N = 8
const CACHE_SCOPE = 'video-count-by-language'

const extractCountRows = response => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.data?.items)) return response.data.items
  if (Array.isArray(response?.data?.response?.items)) return response.data.response.items
  if (Array.isArray(response?.data?.response)) return response.data.response
  if (Array.isArray(response?.response?.items)) return response.response.items
  if (Array.isArray(response?.items)) return response.items
  return []
}

const normalizeRows = raw => {
  if (!Array.isArray(raw)) return []
  return raw
    .map(item => ({
      languageName: String(item?.languageName || item?.language || item?.name || 'Unknown'),
      videoCount: Number(item?.videoCount ?? item?.count ?? item?.total ?? 0) || 0
    }))
    .filter(item => item.languageName)
    .sort((a, b) => {
      if (b.videoCount !== a.videoCount) return b.videoCount - a.videoCount
      return a.languageName.localeCompare(b.languageName)
    })
}

const rowsHaveChanges = (currentRows, nextRows) => {
  const current = normalizeRows(currentRows)
  const next = normalizeRows(nextRows)
  if (current.length !== next.length) return true
  const currentMap = new Map(current.map(row => [row.languageName, row.videoCount]))
  return next.some(row => currentMap.get(row.languageName) !== row.videoCount)
}

const buildPieData = sortedRows => {
  if (!sortedRows.length) return []
  if (sortedRows.length <= PIE_TOP_N) {
    return sortedRows.map(row => ({ name: row.languageName, value: row.videoCount }))
  }
  const top = sortedRows.slice(0, PIE_TOP_N)
  const others = sortedRows.slice(PIE_TOP_N).reduce((sum, row) => sum + row.videoCount, 0)
  return [
    ...top.map(row => ({ name: row.languageName, value: row.videoCount })),
    ...(others > 0 ? [{ name: 'Others', value: others }] : [])
  ]
}

const StatCard = ({ icon, label, value, sub }) => (
  <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 0 2rem 0 rgba(136,152,170,.15)' }}>
    <CardContent>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, lineHeight: 1.2 }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {sub}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: '#fff',
            opacity: 0.9
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
)

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        boxShadow: 1
      }}
    >
      <Typography variant="subtitle2" fontWeight={700}>
        {row?.fullName || label || row?.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Videos: {formatIndianNumber(row?.count ?? row?.value ?? payload[0]?.value, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })}
      </Typography>
    </Box>
  )
}

export const VideoCountByLanguage = () => {
  const dispatch = useDispatch()
  const userData = useSelector(state => state?.userData)

  const cacheKey = useMemo(
    () => buildTabCacheKey(userData?.id || userData?.email || 'admin', CACHE_SCOPE),
    [userData?.id, userData?.email]
  )

  const initialCache = cacheKey ? getTabCache(cacheKey) : null
  const rowsRef = useRef(initialCache?.rows || [])
  const lastCacheKeyRef = useRef(null)

  const [rows, setRows] = useState(() => initialCache?.rows || [])
  const [pageLoading, setPageLoading] = useState(() => initialCache == null)
  const [pendingUpdates, setPendingUpdates] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const [searchText, setSearchText] = useState('')

  rowsRef.current = rows

  const adminUserNameHeader =
    userData?.email || userData?.emailId || userData?.userName || ''

  const videoApiHeaders = useMemo(() => {
    return adminUserNameHeader ? { userName: adminUserNameHeader } : {}
  }, [adminUserNameHeader])

  const sortedRows = useMemo(() => normalizeRows(rows), [rows])

  const totalVideos = useMemo(
    () => sortedRows.reduce((sum, row) => sum + row.videoCount, 0),
    [sortedRows]
  )

  const topLanguage = sortedRows[0]

  const chartBarData = useMemo(
    () =>
      sortedRows.map(row => ({
        name:
          row.languageName.length > 14
            ? `${row.languageName.slice(0, 12)}…`
            : row.languageName,
        fullName: row.languageName,
        count: row.videoCount
      })),
    [sortedRows]
  )

  const pieData = useMemo(() => buildPieData(sortedRows), [sortedRows])

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    if (!q) return sortedRows
    return sortedRows.filter(row => row.languageName.toLowerCase().includes(q))
  }, [sortedRows, searchText])

  const requestVideoCount = async headers => {
    const res = await ApiCaller.get(
      {},
      urls.videoCountByLanguage,
      userData?.access_token,
      headers
    )
    return res?.response?.json()
  }

  const fetchVideoCountByLanguage = async (silent = false) => {
    if (!userData?.access_token) return

    try {
      if (!silent) {
        dispatch(uplodateLoading(true))
        setPageLoading(true)
      }
      setErrorMsg('')
      const response = await requestVideoCount(videoApiHeaders)

      if (response?.status == 200 || response?.successful) {
        let apiData = extractCountRows(response)

        if (!apiData?.length && adminUserNameHeader) {
          const fallbackResponse = await requestVideoCount({})
          if (fallbackResponse?.status == 200 || fallbackResponse?.successful) {
            apiData = extractCountRows(fallbackResponse)
          }
        }

        const normalized = normalizeRows(apiData)

        if (silent && getTabCache(cacheKey) != null) {
          if (rowsHaveChanges(rowsRef.current, normalized)) {
            setPendingUpdates({ rows: normalized })
          } else {
            setPendingUpdates(null)
            setTabCache(cacheKey, { rows: normalized })
          }
        } else {
          setRows(normalized)
          setPendingUpdates(null)
          setTabCache(cacheKey, { rows: normalized })
        }
      } else {
        if (!silent) {
          setRows([])
        }
        const message = response?.message || 'Failed to fetch video language count'
        setErrorMsg(message)
        showDangerToast(message)
      }
    } catch (error) {
      if (!silent) {
        setRows([])
      }
      const message = error?.message || 'Failed to fetch video language count'
      setErrorMsg(message)
      showDangerToast(message)
    } finally {
      if (!silent) {
        dispatch(uplodateLoading(false))
        setPageLoading(false)
      }
    }
  }

  useEffect(() => {
    dispatch(updateTopHeader('Video Count By Language'))
  }, [dispatch])

  useEffect(() => {
    if (!userData?.access_token || !cacheKey || lastCacheKeyRef.current === cacheKey) return
    lastCacheKeyRef.current = cacheKey

    const cached = getTabCache(cacheKey)
    if (cached != null) {
      setRows(cached.rows || [])
      setPageLoading(false)
      fetchVideoCountByLanguage(true)
      return
    }

    fetchVideoCountByLanguage(false)
  }, [userData?.access_token, cacheKey])

  const formatCount = n =>
    formatIndianNumber(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 })

  const emptyState = (
    <Box className="text-center" py={6}>
      <img width="22%" src={NoDataImg} alt="No data" />
      <Typography fontWeight={700} sx={{ mt: 2 }}>
        {pageLoading ? 'Loading…' : errorMsg || 'No data found'}
      </Typography>
    </Box>
  )

  const handleRefresh = () => {
    setPendingUpdates(null)
    fetchVideoCountByLanguage(false)
  }

  const applyPendingUpdates = () => {
    if (!pendingUpdates?.rows) return
    setRows(pendingUpdates.rows)
    setTabCache(cacheKey, { rows: pendingUpdates.rows })
    setPendingUpdates(null)
  }

  return (
    <>
      <NavBar />
      <div className="main-content">
        <NavBarTop />
        <div className="header">
          <div className="logo">
            <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8"></div>
          </div>
        </div>

        <div className="container-fluid mt--7">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
            <Box>
              <Typography variant="h3" fontWeight={700}>
                Video Count by Language
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Distribution of videos across languages
              </Typography>
            </Box>
            <Button
              variant="contained"
              className="custom-filter-btn"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Box>

          {pendingUpdates && (
            <div
              className="alert alert-info d-flex justify-content-between align-items-center mb-3 text-dark"
              style={{ borderRadius: '8px', borderLeft: '5px solid #11cdef' }}
            >
              <span>New video language count updates are available.</span>
              <Button variant="contained" color="info" size="small" onClick={applyPendingUpdates}>
                Update View
              </Button>
            </div>
          )}

          {sortedRows.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<VideoLibraryIcon />}
                  label="Total videos"
                  value={formatCount(totalVideos)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<LanguageIcon />}
                  label="Languages"
                  value={formatCount(sortedRows.length)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<TrendingUpIcon />}
                  label="Top language"
                  value={topLanguage?.languageName || '—'}
                  sub={
                    topLanguage
                      ? `${formatCount(topLanguage.videoCount)} videos (${totalVideos ? ((topLanguage.videoCount / totalVideos) * 100).toFixed(1) : 0}%)`
                      : ''
                  }
                />
              </Grid>
            </Grid>
          )}

          <Card sx={{ borderRadius: 2, boxShadow: '0 0 2rem 0 rgba(136,152,170,.15)' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<BarChartIcon />} iconPosition="start" label="Bar chart" />
              <Tab icon={<PieChartIcon />} iconPosition="start" label="Distribution" />
              <Tab icon={<TableChartIcon />} iconPosition="start" label="Data table" />
            </Tabs>

            <CardContent sx={{ pt: 3 }}>
              {!sortedRows.length ? (
                emptyState
              ) : (
                <>
                  {activeTab === 0 && (
                    <Box>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        Videos per language
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Horizontal bars sorted by count (highest first)
                      </Typography>
                      <Box sx={{ width: '100%', height: Math.max(320, chartBarData.length * 36) }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={chartBarData}
                            layout="vertical"
                            margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                            <XAxis type="number" tickFormatter={v => formatCount(v)} />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={120}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip content={<ChartTooltip />} />
                            <Bar dataKey="count" name="Videos" radius={[0, 4, 4, 0]}>
                              {chartBarData.map((_, index) => (
                                <Cell
                                  key={`bar-${index}`}
                                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  )}

                  {activeTab === 1 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={7}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Share by language
                        </Typography>
                        <Box sx={{ width: '100%', height: 360 }}>
                          <ResponsiveContainer>
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={70}
                                outerRadius={120}
                                paddingAngle={2}
                                dataKey="value"
                                label={({ name, percent }) =>
                                  `${name} (${(percent * 100).toFixed(0)}%)`
                                }
                                labelLine={{ stroke: '#8898aa' }}
                              >
                                {pieData.map((_, index) => (
                                  <Cell
                                    key={`pie-${index}`}
                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip content={<ChartTooltip />} />
                              <Legend layout="horizontal" verticalAlign="bottom" />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Breakdown
                        </Typography>
                        <Box sx={{ maxHeight: 360, overflow: 'auto', pr: 1 }}>
                          {pieData.map((item, index) => {
                            const pct = totalVideos
                              ? ((item.value / totalVideos) * 100).toFixed(1)
                              : 0
                            return (
                              <Box key={item.name} sx={{ mb: 2 }}>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: CHART_COLORS[index % CHART_COLORS.length]
                                      }}
                                    />
                                    <Typography variant="body2" fontWeight={600}>
                                      {item.name}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatCount(item.value)} ({pct}%)
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={Number(pct)}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: '#e9ecef',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: CHART_COLORS[index % CHART_COLORS.length],
                                      borderRadius: 4
                                    }
                                  }}
                                />
                              </Box>
                            )
                          })}
                        </Box>
                      </Grid>
                    </Grid>
                  )}

                  {activeTab === 2 && (
                    <Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                        <Typography variant="h6" fontWeight={600}>
                          All languages
                        </Typography>
                        <TextField
                          size="small"
                          placeholder="Search language…"
                          value={searchText}
                          onChange={e => setSearchText(e.target.value)}
                          sx={{ minWidth: 220 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Box>
                      <div className="table-responsive">
                        <table className="table align-items-center table-flush">
                          <thead className="thead-light">
                            <tr>
                              <th scope="col" style={{ width: 56 }}>
                                #
                              </th>
                              <th scope="col">Language</th>
                              <th scope="col" style={{ width: 140 }}>
                                Videos
                              </th>
                              <th scope="col" style={{ width: 100 }}>
                                Share
                              </th>
                              <th scope="col" style={{ minWidth: 180 }}>
                                Distribution
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRows.length ? (
                              filteredRows.map((item, i) => {
                                const pct = totalVideos
                                  ? (item.videoCount / totalVideos) * 100
                                  : 0
                                return (
                                  <tr key={`${item.languageName}-${i}`}>
                                    <td>{i + 1}</td>
                                    <td>
                                      <Typography fontWeight={600}>{item.languageName}</Typography>
                                    </td>
                                    <td>
                                      <Chip
                                        label={formatCount(item.videoCount)}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    </td>
                                    <td>{pct.toFixed(1)}%</td>
                                    <td>
                                      <LinearProgress
                                        variant="determinate"
                                        value={pct}
                                        sx={{
                                          height: 8,
                                          borderRadius: 4,
                                          bgcolor: '#e9ecef',
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor: '#5e72e4',
                                            borderRadius: 4
                                          }
                                        }}
                                      />
                                    </td>
                                  </tr>
                                )
                              })
                            ) : (
                              <tr>
                                <td colSpan={5} className="text-center text-muted py-4">
                                  No languages match your search
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        Showing {filteredRows.length} of {sortedRows.length} languages · Total{' '}
                        {formatCount(totalVideos)} videos
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}
